// Reading a GitLab issue body — the ONE parser.
//
// A ticket's text is one block of markdown that four writers rewrite. Each of
// them used to keep only the headings it knew and throw the rest away, which is
// why an Accept deleted a hand-written `## Notes`, why the editor opened a
// refined ticket with empty fields, and why criteria written in bold by the chat
// flow were invisible to the editor.
//
// This module reads a body ONCE, into named sections plus everything it does not
// recognise, and `render.ts` writes it back. A writer names only the sections it
// owns; everything else is carried verbatim, in its place.
//
// Ships to the browser: no `node:` API, no regex lookbehind.

import {
  BACKLINK_LINE_RE,
  DOR_REASON_ONLY_LINE_RE,
  DOR_REASON_TRAILING_RE,
  fencedLineIndices,
  H,
  HTML_COMMENT_LINE_RE,
  isSectionHeadingLine,
  keyOfHeadingLine,
  LEGACY_CRITERIA_LABEL,
  LIST_ROW_RE,
  MARK,
  renderCriteriaRows,
  stripListMarker,
  trimBlankLines,
  type KnownKey,
} from "./tokens.js";

// ---------------------------------------------------------------------------
// Marked blocks — `## Build notes`, `## Design`, `## From mail` and
// `## From meeting`.
//
// Marker-delimited, not heading-delimited, and that is the point: a `##\s+Name`
// pattern is one more way for a user sentence to be vacuumed into, or to
// truncate, a machine-owned section. Two `indexOf`s cannot do that.
//
// PLACEMENT is heading-FIRST, marker second, because every other reader of a
// body is heading-delimited and a bare marker line is a boundary to none of
// them: with the marker first, the block is simply the last line of whatever
// section precedes it — which is how `<!-- loom:build-notes -->` once reached
// the editor AS an acceptance criterion. The heading therefore lives OUTSIDE the
// markers, and extraction has to widen back over it.
// ---------------------------------------------------------------------------

/**
 * Offset a marked block starts at: `heading` when it sits directly in front of
 * `begin` (blank lines allowed), else `begin` itself.
 *
 * A backwards character scan, NOT a regex lookbehind. The heading must START a
 * line, or we would be slicing into a user sentence that merely ends in those
 * characters. The blank-line tolerance is what lets the renderer write
 * `## Design\n\n<!-- loom:design -->` and still round-trip.
 */
export const markedBlockStart = (
  body: string,
  begin: number,
  heading: string,
): number => {
  let i = begin;
  while (i > 0 && /[ \t\r\n]/u.test(body[i - 1] ?? "")) i -= 1;
  const headingAt = i - heading.length;
  if (headingAt < 0) return begin;
  if (body.slice(headingAt, i) !== heading) return begin;
  if (headingAt > 0 && body[headingAt - 1] !== "\n") return begin;
  return headingAt;
};

interface MarkedSpan {
  readonly start: number;
  readonly end: number;
  readonly raw: string;
  /** The block's own heading line when the span widened over it, else `""`
   *  (a human deleted the heading, or a legacy marker-first body carries it
   *  INSIDE the span). */
  readonly heading: string;
}

/**
 * The whole block — heading, both markers, everything between — or null.
 *
 * FIRST begin, LAST end: a stray marker pasted into user prose can then only
 * WIDEN the extracted span, never truncate it, so a round-trip cannot silently
 * drop the tail of a section the server owns.
 */
const markedBlockSpan = (
  text: string,
  begin: string,
  end: string,
  heading: string,
): MarkedSpan | null => {
  const b = text.indexOf(begin);
  if (b < 0) return null;
  const e = text.lastIndexOf(end);
  if (e < b) return null;
  const start = markedBlockStart(text, b, heading);
  const stop = e + end.length;
  return {
    start,
    end: stop,
    raw: text.slice(start, stop),
    heading: start < b ? heading : "",
  };
};

/** The block's bytes, or null. The primitive both `extractBuildNotesSection`
 *  and `extractDesignSection` are, and the one {@link parseIssueBody} lifts
 *  with — so the two entry points can never drift. */
export const extractMarkedBlock = (
  body: string | null | undefined,
  begin: string,
  end: string,
  heading: string,
): string | null =>
  body ? (markedBlockSpan(body, begin, end, heading)?.raw ?? null) : null;

/** The body without the block (heading and both markers included). */
export const stripMarkedBlock = (
  body: string,
  begin: string,
  end: string,
  heading: string,
): string => {
  const span = markedBlockSpan(body, begin, end, heading);
  if (span === null) return body.trimEnd();
  return (body.slice(0, span.start) + body.slice(span.end)).trimEnd();
};

/** A list marker, but only where whitespace follows it — so the bold emphasis
 *  of `**Acceptance criteria**` is not mistaken for a bullet. */
const stripLabelMarker = (line: string): string =>
  line.replace(/^\s*(?:[-*+]|\d+[.)])\s+/u, "").trim();

interface Lift extends MarkedSpan {
  readonly key: KnownKey;
}

const BLOCKS = [
  {
    key: "build_notes",
    begin: MARK.buildNotesBegin,
    end: MARK.buildNotesEnd,
    heading: H.buildNotes,
  },
  {
    key: "design",
    begin: MARK.designBegin,
    end: MARK.designEnd,
    heading: H.design,
  },
  {
    key: "from_mail",
    begin: MARK.mailBegin,
    end: MARK.mailEnd,
    heading: H.fromMail,
  },
  {
    key: "from_meeting",
    begin: MARK.meetingBegin,
    end: MARK.meetingEnd,
    heading: H.fromMeeting,
  },
] as const;

/** Every marked block in a body, in source order, non-overlapping. A block
 *  whose span a wider one already covers is left where it is rather than lifted
 *  twice. */
const liftsOf = (text: string): Lift[] => {
  const found: Lift[] = [];
  for (const block of BLOCKS) {
    const span = markedBlockSpan(text, block.begin, block.end, block.heading);
    if (span !== null) found.push({ ...span, key: block.key });
  }
  found.sort((a, z) => a.start - z.start);
  const out: Lift[] = [];
  let at = 0;
  for (const lift of found) {
    if (lift.start < at) continue;
    out.push(lift);
    at = lift.end;
  }
  return out;
};

// ---------------------------------------------------------------------------
// The parsed body.
// ---------------------------------------------------------------------------

/** The brand. It is not a security boundary — it is the mechanism that stops a
 *  writer carrying bytes it never read through this parser, which is what the
 *  old rebuilds did by hand and got wrong six times. */
declare const PARSED: unique symbol;

export interface BodySection {
  /** The heading line exactly as the body writes it (`## What You Need`), or
   *  `""` for a marked block a human stripped the heading off. */
  readonly heading: string;
  /** The section Loom owns, or null for a heading it does not know. */
  readonly key: KnownKey | null;
  /** What sits under the heading: leading blank lines and trailing whitespace
   *  dropped, nothing else normalised. For a marked block, the content from the
   *  BEGIN marker on. */
  readonly body: string;
  /** Heading and body verbatim, `trimEnd()`ed — what a carry writes back. */
  readonly raw: string;
  /** The nearest preceding KNOWN-keyed section in source order, or `"preamble"`.
   *  This is what keeps a hand-written `## Notes` typed under the Description
   *  next to the Description after a rebuild reorders the known sections. */
  readonly anchor: KnownKey | "preamble";
}

export interface ParsedIssueBody {
  readonly [PARSED]: true;
  /** The FIRST `_Part of #N_` line anywhere in the body, lifted out. It sits in
   *  front of every `## ` heading, which is exactly what made it invisible to
   *  every heading-delimited reader. */
  readonly backlink: string | null;
  /** The text before the first `## ` line, after the backlink and any legacy
   *  criteria block are lifted out. The story sentence on a refined body; the
   *  whole text on a plain one. */
  readonly preamble: string;
  /** Rows lifted from a `**Acceptance criteria**` LABEL block inside the
   *  preamble — the shape `create_issue_with_link_proposals` writes — and only
   *  when the body carries no `## Acceptance criteria` section. Null otherwise.
   *  The preamble no longer contains them, and {@link sections} carries a
   *  synthesised `acceptance_criteria` section at the block's own position, so
   *  a `## Context` written after it still renders after it. */
  readonly legacyCriteria: readonly string[] | null;
  /** Source order. A repeated known heading yields several entries with the
   *  same key; {@link sectionOf} decides which one the readers see. */
  readonly sections: readonly BodySection[];
  /** The payload of the TRAILING `<!-- loom:dor-reason: … -->` marker. Every
   *  other marker-only line is dropped: the renderer writes the one current
   *  marker LAST, and a stale copy left mid-body is the one `projectIssue`
   *  reads first. */
  readonly dorReason: string | null;
}

/**
 * Read a body into named sections plus everything it does not recognise.
 *
 * Fixed order, because each step decides what the next one can see:
 *   1. the trailing DoR-reason marker, then every stray marker-only line;
 *   2. the marked blocks, lifted whole (first BEGIN, last END, widened back
 *      over the heading) and remembered where they sat — so a heading-less
 *      block can never be swallowed by the section above it;
 *   3. the backlink line;
 *   4. the `## ` split, on the boundary rule and no other — code fences first,
 *      so an example heading inside one is content, not a section;
 *   5. the legacy `**Acceptance criteria**` label block, lifted out of the
 *      preamble into a synthesised section.
 */
export const parseIssueBody = (
  body: string | null | undefined,
): ParsedIssueBody => {
  const text = body ?? "";

  // (1) The trailing marker, then stray copies of it.
  const trailing = DOR_REASON_TRAILING_RE.exec(text);
  const dorReason = trailing ? (trailing[1] ?? "").trim() : null;
  const base = (trailing ? text.slice(0, trailing.index) : text)
    .split("\n")
    .filter((line) => !DOR_REASON_ONLY_LINE_RE.test(line))
    .join("\n");

  // (2) The marked blocks. `rest` is the body without them; `liftAt[n]` is
  // where block `n` sat in `rest`, which is how source order survives the lift.
  const lifts = liftsOf(base);
  const liftAt: number[] = [];
  let rest = "";
  let cut = 0;
  for (const lift of lifts) {
    rest += base.slice(cut, lift.start);
    liftAt.push(rest.length);
    cut = lift.end;
  }
  rest += base.slice(cut);

  const lines = rest.split("\n");
  const lineStart: number[] = [];
  let offset = 0;
  for (const line of lines) {
    lineStart.push(offset);
    offset += line.length + 1;
  }

  // (3) The backlink line.
  const skip = new Set<number>();
  let backlink: string | null = null;
  for (let i = 0; i < lines.length; i += 1) {
    const m = BACKLINK_LINE_RE.exec(lines[i] ?? "");
    if (m !== null) {
      backlink = m[0];
      skip.add(i);
      break;
    }
  }

  // (4) The `## ` split — outside a closed code fence, where a `## ` line is an
  // EXAMPLE of a heading rather than one.
  const fenced = fencedLineIndices(lines);
  const heads: number[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (fenced.has(i)) continue;
    if (!skip.has(i) && isSectionHeadingLine(lines[i] ?? "")) heads.push(i);
  }
  const preambleEnd = heads[0] ?? lines.length;

  // (5) The legacy criteria block, and only when no real section exists.
  //
  // The label needs its OWN marker strip. The editor's `stripListMarker` eats a
  // leading `*` with no space after it, so `**Acceptance criteria**` comes back
  // as `*Acceptance criteria**` and matches nothing — a bullet marker is a
  // marker only when whitespace follows it.
  let legacyCriteria: string[] | null = null;
  let legacyAt = -1;
  const hasCriteriaSection = heads.some(
    (i) => keyOfHeadingLine(lines[i] ?? "") === "acceptance_criteria",
  );
  if (!hasCriteriaSection) {
    for (let i = 0; i < preambleEnd; i += 1) {
      if (skip.has(i)) continue;
      const label = stripLabelMarker(lines[i] ?? "")
        .replace(/:$/u, "")
        .trim();
      if (label !== LEGACY_CRITERIA_LABEL) continue;
      let j = i + 1;
      while (j < preambleEnd && (lines[j] ?? "").trim().length === 0) j += 1;
      const rows: string[] = [];
      while (j < preambleEnd && LIST_ROW_RE.test(lines[j] ?? "")) {
        rows.push(stripListMarker(lines[j] ?? ""));
        j += 1;
      }
      // A label with no rows under it is prose, not a criteria block.
      if (rows.length === 0) continue;
      for (let k = i; k < j; k += 1) skip.add(k);
      legacyCriteria = rows;
      legacyAt = lineStart[i] ?? 0;
      break;
    }
  }

  interface Entry {
    readonly pos: number;
    readonly key: KnownKey | null;
    readonly heading: string;
    readonly body: string;
    readonly raw: string;
  }
  const entries: Entry[] = [];

  heads.forEach((start, n) => {
    const stop = heads[n + 1] ?? lines.length;
    const own: string[] = [];
    for (let i = start; i < stop; i += 1) {
      if (!skip.has(i)) own.push(lines[i] ?? "");
    }
    const headingLine = lines[start] ?? "";
    entries.push({
      pos: lineStart[start] ?? 0,
      key: keyOfHeadingLine(headingLine),
      heading: headingLine,
      body: trimBlankLines(own.slice(1).join("\n")),
      raw: own.join("\n").trimEnd(),
    });
  });

  lifts.forEach((lift, n) => {
    entries.push({
      pos: liftAt[n] ?? 0,
      key: lift.key,
      heading: lift.heading,
      // A marked block's CONTENT begins at its BEGIN marker: the heading is the
      // renderer's to write, so an owned block and a carried one agree.
      body:
        lift.heading.length === 0
          ? lift.raw
          : trimBlankLines(lift.raw.slice(lift.heading.length)),
      raw: lift.raw,
    });
  });

  if (legacyCriteria !== null) {
    const rows = renderCriteriaRows(legacyCriteria) ?? "";
    entries.push({
      pos: legacyAt,
      key: "acceptance_criteria",
      heading: H.acceptanceCriteria,
      body: rows,
      raw: `${H.acceptanceCriteria}\n\n${rows}`,
    });
  }

  entries.sort((a, z) => a.pos - z.pos);

  let lastKnown: KnownKey | "preamble" = "preamble";
  const sections: BodySection[] = entries.map((e) => {
    const anchor = lastKnown;
    if (e.key !== null) lastKnown = e.key;
    return {
      heading: e.heading,
      key: e.key,
      body: e.body,
      raw: e.raw,
      anchor,
    };
  });

  const preLines: string[] = [];
  for (let i = 0; i < preambleEnd; i += 1) {
    if (!skip.has(i)) preLines.push(lines[i] ?? "");
  }

  return {
    backlink,
    preamble: trimBlankLines(preLines.join("\n")),
    legacyCriteria,
    sections,
    dorReason,
  } as unknown as ParsedIssueBody;
};

/**
 * The copy of a repeated heading every reader sees: the LAST one with a
 * non-empty body, else the last one.
 *
 * The editor's rule, near enough to be the same rule: it assigns each matching
 * chunk in turn, so the last copy wins — except that it skips a chunk with no
 * line break at all. The difference is only a LAST copy that is a bare heading
 * over an earlier copy that has content, where this returns the content. That is
 * the safe direction: the alternative shows an empty field for a ticket that has
 * text, which is the bug this module exists to close.
 */
export const sectionOf = (
  parsed: ParsedIssueBody,
  key: KnownKey,
): BodySection | null => {
  const copies = parsed.sections.filter((s) => s.key === key);
  if (copies.length === 0) return null;
  for (let i = copies.length - 1; i >= 0; i -= 1) {
    const copy = copies[i]!;
    if (copy.body.trim().length > 0) return copy;
  }
  return copies[copies.length - 1]!;
};

/** A section's body with the HTML-comment-only lines dropped. A machine marker
 *  is never a field value: the editor writes the parsed draft straight back, so
 *  a marker that surfaced as content would be written to GitLab as content. */
const withoutCommentLines = (text: string): string =>
  text
    .split("\n")
    .filter((line) => !HTML_COMMENT_LINE_RE.test(line))
    .join("\n")
    .trim();

/**
 * The acceptance criteria a body states, as rows without their list markers.
 *
 * The `## Acceptance criteria` section when there is one — including the
 * synthesised one a legacy bold block produced — else the legacy rows, else
 * empty. Markers are stripped a second time AFTER the first strip, because a
 * body an earlier Save corrupted carries the marker WITH a list prefix
 * (`3. <!-- … -->`), which the line-level filter cannot see.
 */
export const criteriaRowsOf = (parsed: ParsedIssueBody): readonly string[] => {
  const section = sectionOf(parsed, "acceptance_criteria");
  if (section === null) return parsed.legacyCriteria ?? [];
  return section.body
    .split("\n")
    .filter((line) => !HTML_COMMENT_LINE_RE.test(line))
    .map(stripListMarker)
    .filter((row) => row.length > 0 && !HTML_COMMENT_LINE_RE.test(row));
};

export interface EditorView {
  readonly description: string;
  /** WHERE the Description came from, so a Save writes it back where it was
   *  rather than minting a `## What you need` section a plain-text ticket never
   *  had. */
  readonly descriptionFrom: "what_you_need" | "preamble";
  readonly outcome: string;
  readonly acceptanceCriteria: string[];
}

/**
 * What the issue editor shows for a body.
 *
 * The Description is `## What you need` when the body has that section, and the
 * PREAMBLE otherwise — which is the half of bug B2 this closes. A refined
 * ticket's story sentence has no heading of its own, so the editor showed an
 * empty Description for 12 of the 20 latest `dor:pass` issues and the user could
 * not edit what was plainly on the ticket.
 *
 * A refined body that carries a link-only `## What you need` (what the refine
 * Accept writes when the requester pasted a URL into the form) still shows the
 * LINK LINE as the Description, not the story. That is the section's own bytes,
 * so it is the honest answer — but it means B2 is only half-closed on that
 * shape, by design.
 */
export const editorViewOf = (parsed: ParsedIssueBody): EditorView => {
  const section = sectionOf(parsed, "what_you_need");
  const outcome = sectionOf(parsed, "business_outcome");
  const hasSection = section !== null && section.body.trim().length > 0;
  return {
    description: hasSection
      ? withoutCommentLines(section.body)
      : withoutCommentLines(parsed.preamble),
    descriptionFrom: hasSection ? "what_you_need" : "preamble",
    outcome: outcome === null ? "" : withoutCommentLines(outcome.body),
    acceptanceCriteria: [...criteriaRowsOf(parsed)],
  };
};

// ---------------------------------------------------------------------------
// Whole-body span helpers — the same boundary rule, on a raw body.
//
// `extractSubIssuesSection` and friends keep their signatures (a body in, bytes
// out) because three call sites outside this package still use them that way.
// They go through the SAME span logic the parser does, so the retired
// `SUB_ISSUES_SECTION_RE` (unanchored, so it matched inside `### Sub-issues`)
// and design-section's `/^##\s/` + `<!-- loom:` stop are gone rather than
// duplicated.
// ---------------------------------------------------------------------------

interface SectionSpan {
  readonly start: number;
  readonly stop: number;
  readonly raw: string;
}

const spanOf = (body: string, key: KnownKey): SectionSpan | null => {
  const trailing = DOR_REASON_TRAILING_RE.exec(body);
  const base = trailing ? body.slice(0, trailing.index) : body;
  const lifts = liftsOf(base);
  const lines = base.split("\n");
  const lineStart: number[] = [];
  let offset = 0;
  for (const line of lines) {
    lineStart.push(offset);
    offset += line.length + 1;
  }
  const fenced = fencedLineIndices(lines);
  const heads: number[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (fenced.has(i)) continue;
    const at = lineStart[i] ?? 0;
    const insideBlock = lifts.some((l) => at > l.start && at < l.end);
    if (!insideBlock && isSectionHeadingLine(lines[i] ?? "")) heads.push(i);
  }
  const stopAfter = (i: number): number => {
    const start = lineStart[i] ?? 0;
    let stop = base.length;
    for (const h of heads) {
      const at = lineStart[h] ?? 0;
      if (at > start && at < stop) stop = at;
    }
    for (const lift of lifts) {
      if (lift.start > start && lift.start < stop) stop = lift.start;
    }
    return stop;
  };
  const copies = heads.filter((i) => keyOfHeadingLine(lines[i] ?? "") === key);
  if (copies.length === 0) return null;
  const spanAt = (i: number): SectionSpan => {
    const start = lineStart[i] ?? 0;
    const stop = stopAfter(i);
    return { start, stop, raw: base.slice(start, stop).trimEnd() };
  };
  for (let n = copies.length - 1; n >= 0; n -= 1) {
    const span = spanAt(copies[n]!);
    if (span.raw.split("\n").slice(1).join("\n").trim().length > 0) return span;
  }
  return spanAt(copies[copies.length - 1]!);
};

/** One known section's bytes, heading included, or null. */
export const extractKnownSection = (
  body: string | null | undefined,
  key: KnownKey,
): string | null => (body ? (spanOf(body, key)?.raw ?? null) : null);

/**
 * The body without that section.
 *
 * The trailing DoR-reason marker goes with it, in every case rather than only
 * when the section happened to be last. It is end-anchored by contract, and both
 * callers append content after this call: leaving it in place strands it
 * mid-body, where the end-anchored strip can no longer see it and the raw
 * comment leaks into the editor. The writers re-stamp the current one LAST.
 */
export const stripKnownSection = (
  body: string | null | undefined,
  key: KnownKey,
): string => {
  const text = body ?? "";
  const trailing = DOR_REASON_TRAILING_RE.exec(text);
  const base = trailing ? text.slice(0, trailing.index) : text;
  const span = spanOf(text, key);
  if (span === null) return base.trimEnd();
  let start = span.start;
  while (start > 0 && base[start - 1] === "\n") start -= 1;
  const stop = span.stop >= base.length ? base.length : span.stop - 1;
  return (base.slice(0, start) + base.slice(stop)).trimEnd();
};
